import type { Href } from "expo-router";
import { router, useLocalSearchParams } from "expo-router";
import { useCallback, useState } from "react";
import {
  ActivityIndicator,
  Linking,
  Pressable,
  ScrollView,
  StatusBar,
  Text,
  useWindowDimensions,
  View,
} from "react-native";
import { COLORS, FONTS } from "../lib/brand";
import { LogoMark, LogoWordmark } from "../components/brand/Logo";
import { PANE_THEMES } from "../components/brand/paneThemes";
import Svg, { Circle, Line, Rect } from "react-native-svg";
import { useSafeAreaInsets } from "react-native-safe-area-context";

import { submitInquiry, type InquiryChannel } from "../lib/inquiries";
import { useMarketingFonts } from "../lib/useMarketingFonts";
import { MarketingHead } from "../components/MarketingHead";
import { META_CONTACT } from "../lib/marketingMeta";
import { Button, FormField, Input } from "../components/ui";

const FOREST = COLORS.FOREST.hex;
const FOREST_MID = COLORS.FOREST_MID.hex;
const SAND = COLORS.SAND.hex;
const SAND_DARK = COLORS.SAND_DARK.hex;
const AMBER = COLORS.AMBER.hex;
const LINEN = COLORS.LINEN.hex;
const WHITE = COLORS.OFFW.hex;
const SAGE = COLORS.SAGE.hex;
/** Required-field asterisks AND the inline form-error text. Tuned for the
 *  sand/linen contact surface — reads as "important" without being garish. */
const REQUIRED_RED = PANE_THEMES.Student.destructive;

type TabKey = InquiryChannel;

const TABS: { key: TabKey; label: string }[] = [
  { key: "company", label: "Company Inquiry" },
  { key: "student", label: "Student Question" },
  { key: "partnership", label: "Partnership / Press" },
  { key: "general", label: "General" },
];

const PARTNERSHIP_TYPES: { value: string; label: string }[] = [
  { value: "press", label: "Press" },
  { value: "academic", label: "Academic" },
  { value: "corporate partnership", label: "Corporate partnership" },
  { value: "other", label: "Other" },
];

/** Company tab — "What are you interested in?" radio. The two URL-deep-link
 *  values (rm / licensing) are pre-selected when the user lands via the
 *  /contact?topic=rm or ?topic=licensing CTAs on the marketing landing. */
const COMPANY_INTERESTS: { value: string; label: string; sub: string }[] = [
  { value: "rm", label: "Coravu Managed", sub: "Coravu designs and runs the program for you" },
  { value: "licensing", label: "Self-Managed", sub: "Your team designs and runs the program; Coravu provides the platform" },
  { value: "both", label: "Both", sub: "I'd like to hear about both" },
  { value: "exploring", label: "Not sure yet", sub: "Just exploring options" },
];

function padX(width: number): number {
  return width >= 760 ? 56 : 28;
}

function isValidEmail(email: string): boolean {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim());
}

/** Student tab: require an email that looks like a school address (per product test plan). */
function isUniversityEmail(email: string): boolean {
  if (!isValidEmail(email)) return false;
  const domain = email.trim().toLowerCase().split("@")[1] ?? "";
  return (
    domain.endsWith(".edu") ||
    domain.endsWith(".ac.uk") ||
    domain.endsWith(".edu.au") ||
    domain.endsWith(".ac.jp")
  );
}

function FormInput(props: {
  label: string;
  value: string;
  onChangeText: (t: string) => void;
  placeholder: string;
  keyboardType?: "default" | "email-address";
  multiline?: boolean;
  required?: boolean;
}) {
  return (
    <Input
      label={props.label}
      value={props.value}
      onChangeText={props.onChangeText}
      placeholder={props.placeholder}
      keyboardType={props.keyboardType ?? "default"}
      multiline={props.multiline}
      required={props.required}
      variant="light"
    />
  );
}

/** Builds an inline error message that names the missing fields, instead of
 *  the generic "Please fill in all fields". */
function missingFieldsMessage(
  pairs: Array<readonly [label: string, value: string]>
): string | null {
  const missing = pairs.filter(([, v]) => !v.trim()).map(([label]) => label);
  if (missing.length === 0) return null;
  if (missing.length === 1) return `${missing[0]} is required.`;
  if (missing.length === 2) return `${missing[0]} and ${missing[1]} are required.`;
  return `${missing.slice(0, -1).join(", ")}, and ${missing[missing.length - 1]} are required.`;
}

export default function ContactScreen() {
  const fontsReady = useMarketingFonts();
  const { width: winW } = useWindowDimensions();
  const insets = useSafeAreaInsets();
  const pad = padX(winW);
  const twoCol = winW >= 760;

  // Honor /contact?topic=rm | ?topic=licensing from the landing CTAs. Any
  // other value is ignored; we keep the default "Company" tab + "exploring"
  // selection so the page still works for non-deep-link visits.
  const params = useLocalSearchParams<{ topic?: string }>();
  const topicParam = Array.isArray(params.topic) ? params.topic[0] : params.topic;
  const initialTopic =
    topicParam === "rm" || topicParam === "licensing" ? topicParam : null;

  // initialTopic forces "company" tab; default is already "company" so the
  // value is the same either way today, but expressed via the param so the
  // intent ("the deep-link drove this") is readable.
  const [tab, setTab] = useState<TabKey>("company");
  const [thankYou, setThankYou] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);

  const [coFirst, setCoFirst] = useState("");
  const [coLast, setCoLast] = useState("");
  const [coCompany, setCoCompany] = useState("");
  const [coEmail, setCoEmail] = useState("");
  const [coMessage, setCoMessage] = useState("");
  const [coInquiryType, setCoInquiryType] = useState<string>(initialTopic ?? "exploring");

  const [stFirst, setStFirst] = useState("");
  const [stLast, setStLast] = useState("");
  const [stEmail, setStEmail] = useState("");
  const [stUniversity, setStUniversity] = useState("");
  const [stMessage, setStMessage] = useState("");

  const [ptFirst, setPtFirst] = useState("");
  const [ptLast, setPtLast] = useState("");
  const [ptOrg, setPtOrg] = useState("");
  const [ptEmail, setPtEmail] = useState("");
  const [ptType, setPtType] = useState(PARTNERSHIP_TYPES[0]!.value);
  const [ptMessage, setPtMessage] = useState("");

  const [genName, setGenName] = useState("");
  const [genEmail, setGenEmail] = useState("");
  const [genMessage, setGenMessage] = useState("");

  const resetThankYou = useCallback(() => {
    setThankYou(false);
    setFormError(null);
  }, []);

  const onSubmitCompany = useCallback(async () => {
    setFormError(null);
    const missing = missingFieldsMessage([
      ["First name", coFirst],
      ["Last name", coLast],
      ["Company", coCompany],
      ["Work email", coEmail],
      ["Message", coMessage],
    ]);
    if (missing) {
      setFormError(missing);
      return;
    }
    const name = `${coFirst} ${coLast}`.trim();
    if (!isValidEmail(coEmail)) {
      setFormError("Please enter a valid work email.");
      return;
    }
    setSubmitting(true);
    const { error } = await submitInquiry({
      channel: "company",
      name,
      email: coEmail,
      message: coMessage,
      organization: coCompany,
      inquiry_type: coInquiryType,
    });
    setSubmitting(false);
    if (error) {
      setFormError(error.message);
      return;
    }
    setThankYou(true);
  }, [coFirst, coLast, coCompany, coEmail, coMessage, coInquiryType]);

  const onSubmitStudent = useCallback(async () => {
    setFormError(null);
    const missing = missingFieldsMessage([
      ["First name", stFirst],
      ["Last name", stLast],
      ["University email", stEmail],
      ["University", stUniversity],
      ["Message", stMessage],
    ]);
    if (missing) {
      setFormError(missing);
      return;
    }
    const name = `${stFirst} ${stLast}`.trim();
    if (!isUniversityEmail(stEmail)) {
      setFormError("Please use your university email (.edu, .ac.uk, etc.).");
      return;
    }
    setSubmitting(true);
    const { error } = await submitInquiry({
      channel: "student",
      name,
      email: stEmail,
      message: stMessage,
      university: stUniversity,
    });
    setSubmitting(false);
    if (error) {
      setFormError(error.message);
      return;
    }
    setThankYou(true);
  }, [stFirst, stLast, stEmail, stUniversity, stMessage]);

  const onSubmitPartnership = useCallback(async () => {
    setFormError(null);
    const missing = missingFieldsMessage([
      ["First name", ptFirst],
      ["Last name", ptLast],
      ["Organization", ptOrg],
      ["Email", ptEmail],
      ["Message", ptMessage],
    ]);
    if (missing) {
      setFormError(missing);
      return;
    }
    const name = `${ptFirst} ${ptLast}`.trim();
    if (!isValidEmail(ptEmail)) {
      setFormError("Please enter a valid email.");
      return;
    }
    setSubmitting(true);
    const { error } = await submitInquiry({
      channel: "partnership",
      name,
      email: ptEmail,
      message: ptMessage,
      organization: ptOrg,
      inquiry_type: ptType,
    });
    setSubmitting(false);
    if (error) {
      setFormError(error.message);
      return;
    }
    setThankYou(true);
  }, [ptFirst, ptLast, ptOrg, ptEmail, ptType, ptMessage]);

  const onSubmitGeneral = useCallback(async () => {
    setFormError(null);
    const missing = missingFieldsMessage([
      ["Name", genName],
      ["Email", genEmail],
      ["Message", genMessage],
    ]);
    if (missing) {
      setFormError(missing);
      return;
    }
    if (!isValidEmail(genEmail)) {
      setFormError("Please enter a valid email.");
      return;
    }
    setSubmitting(true);
    const { error } = await submitInquiry({
      channel: "general",
      name: genName,
      email: genEmail,
      message: genMessage,
    });
    setSubmitting(false);
    if (error) {
      setFormError(error.message);
      return;
    }
    setThankYou(true);
  }, [genName, genEmail, genMessage]);

  const submitCurrent = useCallback(() => {
    if (tab === "company") void onSubmitCompany();
    else if (tab === "student") void onSubmitStudent();
    else if (tab === "partnership") void onSubmitPartnership();
    else void onSubmitGeneral();
  }, [tab, onSubmitCompany, onSubmitStudent, onSubmitPartnership, onSubmitGeneral]);

  if (!fontsReady) {
    return (
      <View style={{ flex: 1, backgroundColor: LINEN, justifyContent: "center", alignItems: "center" }}>
        <StatusBar barStyle="dark-content" />
        <ActivityIndicator color={AMBER} />
      </View>
    );
  }

  const leftBlock = (
    <View
      style={{
        paddingHorizontal: pad,
        paddingVertical: twoCol ? 64 : 48,
        borderRightWidth: twoCol ? 1 : 0,
        borderBottomWidth: twoCol ? 0 : 1,
        borderColor: SAND_DARK,
        justifyContent: "center",
      }}
    >
      <View>
        <Text
          style={{
            fontFamily: FONTS.bodyBold,
            fontSize: 10,
            letterSpacing: 2,
            textTransform: "uppercase",
            color: AMBER,
          }}
        >
          Get in touch
        </Text>
        <Text
          style={{
            marginTop: 6,
            fontSize: 31,
            lineHeight: 36,
            fontFamily: FONTS.display,
            color: FOREST,
          }}
        >
          Let's talk{" "}
          <Text style={{ fontFamily: FONTS.displayItalic, color: AMBER }}>
            recruiting.
          </Text>
        </Text>
        <Text
          style={{
            marginTop: 12,
            fontFamily: FONTS.body,
            fontSize: 13,
            lineHeight: 24,
            color: "rgba(28,43,26,0.65)",
          }}
        >
          Whether you're a company exploring a program, a student with questions, or just curious — we respond within 1–2 business days.
        </Text>
        <Text
          style={{
            marginTop: 24,
            fontFamily: FONTS.bodyLight,
            fontSize: 14,
            lineHeight: 24,
            color: "rgba(28,43,26,0.55)",
            maxWidth: 380,
          }}
        >
          Prefer email? Reach us at{" "}
          <Text
            onPress={() => void Linking.openURL("mailto:hello@coravu.com")}
            style={{ color: AMBER, textDecorationLine: "underline" }}
          >
            hello@coravu.com
          </Text>{" "}
          — we route to the right person within one business day.
        </Text>
      </View>
    </View>
  );

  const tabRow = (
    // Ground line lives on the OUTER wrapper so it spans the full form column,
    // not just the width of the 4 tab pressables inside the horizontal
    // ScrollView. The active-tab AMBER 2px borderBottom + marginBottom: -1
    // still overlaps cleanly — now against this full-width line.
    <View
      style={{
        marginBottom: 28,
        borderBottomWidth: 1,
        borderBottomColor: SAND_DARK,
      }}
    >
      <ScrollView horizontal showsHorizontalScrollIndicator={false}>
        <View style={{ flexDirection: "row" }}>
          {TABS.map((t) => {
            const active = tab === t.key;
            return (
              <Pressable
                key={t.key}
                onPress={() => {
                  setTab(t.key);
                  setFormError(null);
                  setThankYou(false);
                }}
                style={{
                  paddingVertical: 9,
                  paddingHorizontal: 18,
                  borderBottomWidth: 2,
                  borderBottomColor: active ? AMBER : "transparent",
                  marginBottom: -1,
                }}
              >
                <Text
                  style={{
                    fontFamily: FONTS.bodyBold,
                    fontSize: 11.2,
                    letterSpacing: 1.4,
                    textTransform: "uppercase",
                    color: active ? FOREST : "rgba(28,43,26,0.35)",
                  }}
                >
                  {t.label}
                </Text>
              </Pressable>
            );
          })}
        </View>
      </ScrollView>
    </View>
  );

  const thankYouBlock = (
    <View style={{ gap: 16 }}>
      <Text style={{ fontFamily: FONTS.displayMedium, fontSize: 28, color: FOREST }}>Thank you</Text>
      <Text style={{ fontFamily: FONTS.body, fontSize: 15, lineHeight: 24, color: "rgba(28,43,26,0.65)" }}>
        {"We'll get back to you within 1–2 business days."}
      </Text>
      <Pressable
        onPress={resetThankYou}
        style={{
          alignSelf: "flex-start",
          borderWidth: 1,
          borderColor: SAND_DARK,
          paddingVertical: 12,
          paddingHorizontal: 18,
          borderRadius: 2,
          backgroundColor: LINEN,
        }}
      >
        <Text style={{ fontFamily: FONTS.bodySemiBold, fontSize: 12, letterSpacing: 1.2, color: FOREST }}>
          Send another message
        </Text>
      </Pressable>
    </View>
  );

  const companyForm = (
    <View style={{ gap: 12 }}>
      <View style={{ flexDirection: twoCol ? "row" : "column", gap: 12 }}>
        <View style={{ flex: 1 }}>
          <FormInput label="First Name" value={coFirst} onChangeText={setCoFirst} placeholder="Jane" required />
        </View>
        <View style={{ flex: 1 }}>
          <FormInput label="Last Name" value={coLast} onChangeText={setCoLast} placeholder="Smith" required />
        </View>
      </View>
      <View>
        <FormInput label="Company" value={coCompany} onChangeText={setCoCompany} placeholder="Acme Corp" required />
      </View>
      <FormField label="What are you interested in?">
        <View style={{ flexDirection: "row", flexWrap: "wrap", gap: 8 }}>
          {COMPANY_INTERESTS.map((opt) => {
            const sel = coInquiryType === opt.value;
            return (
              <Pressable
                key={opt.value}
                onPress={() => setCoInquiryType(opt.value)}
                style={{
                  paddingVertical: 8,
                  paddingHorizontal: 12,
                  borderRadius: 2,
                  borderWidth: 1,
                  borderColor: sel ? AMBER : SAND_DARK,
                  backgroundColor: sel ? "rgba(181,135,58,0.12)" : LINEN,
                  minWidth: 0,
                }}
              >
                <Text style={{ fontFamily: FONTS.bodyMedium, fontSize: 12, color: FOREST }}>
                  {opt.label}
                </Text>
                <Text
                  style={{
                    fontFamily: FONTS.body,
                    fontSize: 10.5,
                    color: "rgba(28,43,26,0.5)",
                    marginTop: 1,
                  }}
                >
                  {opt.sub}
                </Text>
              </Pressable>
            );
          })}
        </View>
      </FormField>
      <View>
        <FormInput label="Work Email" value={coEmail} onChangeText={setCoEmail} placeholder="jane@company.com" keyboardType="email-address" required />
      </View>
      <View>
        <FormInput label="What are you looking for?" value={coMessage} onChangeText={setCoMessage} placeholder={"Roles you're hiring for..."} multiline required />
      </View>
      <Text style={{ fontFamily: FONTS.bodyItalic, fontSize: 10.6, color: "rgba(28,43,26,0.52)", lineHeight: 16 }}>
        For a full program inquiry, email{" "}
        <Text onPress={() => void Linking.openURL("mailto:Client@coravu.com")} style={{ color: AMBER }}>
          Client@coravu.com
        </Text>
        .
      </Text>
    </View>
  );

  const studentForm = (
    <View style={{ gap: 12 }}>
      <View style={{ flexDirection: twoCol ? "row" : "column", gap: 12 }}>
        <View style={{ flex: 1 }}>
          <FormInput label="First Name" value={stFirst} onChangeText={setStFirst} placeholder="Alex" required />
        </View>
        <View style={{ flex: 1 }}>
          <FormInput label="Last Name" value={stLast} onChangeText={setStLast} placeholder="Johnson" required />
        </View>
      </View>
      <View>
        <FormInput label="University Email" value={stEmail} onChangeText={setStEmail} placeholder="you@university.edu" keyboardType="email-address" required />
      </View>
      <View>
        <FormInput label="University" value={stUniversity} onChangeText={setStUniversity} placeholder="Your university" required />
      </View>
      <View>
        <FormInput label="Your question or message" value={stMessage} onChangeText={setStMessage} placeholder="What can we help you with?" multiline required />
      </View>
      <Text style={{ fontFamily: FONTS.bodyItalic, fontSize: 10.6, color: "rgba(28,43,26,0.52)", lineHeight: 16 }}>
        Want to compete?{" "}
        <Text onPress={() => router.push("/signup?role=student" as Href)} style={{ color: AMBER }}>
          Create your free account →
        </Text>
      </Text>
    </View>
  );

  const partnershipForm = (
    <View style={{ gap: 12 }}>
      <View style={{ flexDirection: twoCol ? "row" : "column", gap: 12 }}>
        <View style={{ flex: 1 }}>
          <FormInput label="First Name" value={ptFirst} onChangeText={setPtFirst} placeholder="Your name" required />
        </View>
        <View style={{ flex: 1 }}>
          <FormInput label="Last Name" value={ptLast} onChangeText={setPtLast} placeholder="Last name" required />
        </View>
      </View>
      <View>
        <FormInput label="Organization" value={ptOrg} onChangeText={setPtOrg} placeholder="Publication, company, or institution" required />
      </View>
      <View>
        <FormInput label="Email" value={ptEmail} onChangeText={setPtEmail} placeholder="your@email.com" keyboardType="email-address" required />
      </View>
      <FormField label="Inquiry type">
        <View style={{ flexDirection: "row", flexWrap: "wrap", gap: 8 }}>
          {PARTNERSHIP_TYPES.map((opt) => {
            const sel = ptType === opt.value;
            return (
              <Pressable
                key={opt.value}
                onPress={() => setPtType(opt.value)}
                style={{
                  paddingVertical: 8,
                  paddingHorizontal: 12,
                  borderRadius: 2,
                  borderWidth: 1,
                  borderColor: sel ? AMBER : SAND_DARK,
                  backgroundColor: sel ? "rgba(181,135,58,0.12)" : LINEN,
                }}
              >
                <Text style={{ fontFamily: FONTS.bodyMedium, fontSize: 12, color: FOREST }}>{opt.label}</Text>
              </Pressable>
            );
          })}
        </View>
      </FormField>
      <View>
        <FormInput
          label="Message"
          value={ptMessage}
          onChangeText={setPtMessage}
          placeholder="Partnership interest, media inquiry..."
          multiline
          required
        />
      </View>
      <Text style={{ fontFamily: FONTS.bodyItalic, fontSize: 10.6, color: "rgba(28,43,26,0.52)", lineHeight: 16 }}>
        You can also reach us at{" "}
        <Text onPress={() => void Linking.openURL("mailto:Press@coravu.com")} style={{ color: AMBER }}>
          Press@coravu.com
        </Text>
        .
      </Text>
    </View>
  );

  const generalForm = (
    <View style={{ gap: 12 }}>
      <View>
        <FormInput label="Name" value={genName} onChangeText={setGenName} placeholder="Your name" required />
      </View>
      <View>
        <FormInput label="Email" value={genEmail} onChangeText={setGenEmail} placeholder="your@email.com" keyboardType="email-address" required />
      </View>
      <View>
        <FormInput label="Message" value={genMessage} onChangeText={setGenMessage} placeholder={"What's on your mind?"} multiline required />
      </View>
    </View>
  );

  const activeForm =
    tab === "company" ? companyForm : tab === "student" ? studentForm : tab === "partnership" ? partnershipForm : generalForm;

  const rightBlock = (
    <View style={{ paddingHorizontal: pad, paddingVertical: twoCol ? 64 : 48, backgroundColor: WHITE, flex: 1 }}>
      {thankYou ? (
        thankYouBlock
      ) : (
        <>
          {tabRow}
          {activeForm}
          {formError ? (
            <Text style={{ fontFamily: FONTS.bodyMedium, fontSize: 13, color: REQUIRED_RED, marginTop: 8 }}>{formError}</Text>
          ) : null}
          <View className="mt-3">
          <Button
            onPress={submitCurrent}
            disabled={submitting}
            loading={submitting}
            variant="cta"
          >
            Send Message
          </Button>
          </View>
          {submitting ? <ActivityIndicator style={{ marginTop: 12 }} color={AMBER} /> : null}
        </>
      )}
    </View>
  );

  const nav = (
    <View
      style={{
        paddingHorizontal: winW >= 760 ? 48 : 24,
        height: 56 + insets.top,
        paddingTop: insets.top,
        flexDirection: "row",
        alignItems: "center",
        justifyContent: "space-between",
        borderBottomWidth: 1,
        borderBottomColor: SAND_DARK,
        backgroundColor: LINEN,
      }}
    >
      <Pressable
        onPress={() => router.replace("/" as Href)}
        style={{ flexDirection: "row", alignItems: "center", gap: 10 }}
        accessibilityRole="link"
      >
        <LogoMark size={30} tile={FOREST_MID} ringOuter={SAGE} ringInner={AMBER} dot={AMBER} />
        <LogoWordmark fontSize={20} letterSpacing={3.2} color={FOREST} accent={AMBER} />
      </Pressable>
      <Pressable onPress={() => router.replace("/" as Href)}>
        <Text style={{ fontFamily: FONTS.bodySemiBold, fontSize: 11.2, letterSpacing: 1.4, textTransform: "uppercase", color: "rgba(28,43,26,0.45)" }}>
          ← Home
        </Text>
      </Pressable>
    </View>
  );

  const footer = (
    <View
      style={{
        minHeight: 56,
        paddingHorizontal: winW >= 760 ? 48 : 24,
        paddingVertical: 12,
        flexDirection: winW >= 520 ? "row" : "column",
        alignItems: winW >= 520 ? "center" : "flex-start",
        justifyContent: "space-between",
        gap: 8,
        borderTopWidth: 1,
        borderTopColor: SAND_DARK,
        backgroundColor: LINEN,
      }}
    >
      <Text style={{ fontFamily: FONTS.body, fontSize: 10.9, color: "rgba(28,43,26,0.3)" }}>
        © 2026 Coravu · Precision Talent · Case Recruitment
      </Text>
      <View style={{ flexDirection: "row", flexWrap: "wrap", gap: 20 }}>
        <Pressable onPress={() => router.replace("/" as Href)}>
          <Text style={{ fontFamily: FONTS.bodyMedium, fontSize: 11.2, color: "rgba(28,43,26,0.35)" }}>Home</Text>
        </Pressable>
        <Pressable onPress={() => router.push("/signup?role=student" as Href)}>
          <Text style={{ fontFamily: FONTS.bodyMedium, fontSize: 11.2, color: "rgba(28,43,26,0.35)" }}>For Students</Text>
        </Pressable>
        <Pressable onPress={() => router.push("/signup?role=client" as Href)}>
          <Text style={{ fontFamily: FONTS.bodyMedium, fontSize: 11.2, color: "rgba(28,43,26,0.35)" }}>For Companies</Text>
        </Pressable>
      </View>
    </View>
  );

  return (
    <View style={{ flex: 1, backgroundColor: LINEN }}>
      <MarketingHead meta={META_CONTACT} />
      <StatusBar barStyle="dark-content" />
      {nav}
      <ScrollView
        keyboardShouldPersistTaps="handled"
        contentContainerStyle={{ flexGrow: 1, paddingBottom: insets.bottom + 12 }}
      >
        <View style={{ flexDirection: twoCol ? "row" : "column", flex: 1 }}>
          <View style={{ flex: twoCol ? 1 : undefined, alignSelf: "stretch" }}>{leftBlock}</View>
          <View style={{ flex: twoCol ? 1 : undefined, alignSelf: "stretch" }}>{rightBlock}</View>
        </View>
      </ScrollView>
      {footer}
    </View>
  );
}
